import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import HeroScene from "@/components/hero/HeroScene";
import { Reveal, Stagger, StaggerItem } from "@/components/anim/Reveal";
import Typewriter from "@/components/anim/Typewriter";
import CTASection from "@/components/site/CTASection";
import { SERVICES, INDUSTRIES } from "@/data/solutions";
import { Compass, ShieldCheck, Users, Sparkles } from "lucide-react";

const HeroSection = () => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  useEffect(() => {
    const handler = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      mouseX.set(x);
      mouseY.set(y);
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, [mouseX, mouseY]);

  const glowX = useSpring(useTransform(mouseX, (v) => -v * 1.4), { stiffness: 60, damping: 20 });
  const glowY = useSpring(useTransform(mouseY, (v) => -v * 1.4), { stiffness: 60, damping: 20 });

  return (
    <section
      className="relative overflow-hidden pt-28 pb-32 md:pt-40 md:pb-48"
      data-testid="hero-section"
    >
      {/* Ambient background */}
      <motion.div
        className="pointer-events-none absolute top-1/2 left-1/2 w-[900px] h-[900px] rounded-full"
        style={{
          translateX: "-50%",
          translateY: "-50%",
          background:
            "radial-gradient(closest-side, rgba(77,215,238,0.10), rgba(77,215,238,0.02) 55%, transparent 80%)",
          x: glowX,
          y: glowY,
        }}
      />

      {/* R3F 3D scene */}
      <HeroScene />

      <div className="fs-container relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <span className="fs-eyebrow">Enterprise Technology · Chennai</span>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="fs-hero-title mt-6">
              Business communication{" "}
              <span className="fs-cyan">infrastructure,</span>
              <br className="hidden md:block" /> engineered for scale.
            </h1>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-8 flex justify-center">
              <div
                className="text-4xl md:text-6xl font-semibold"
                style={{
                  letterSpacing: "-0.02em",
                  minHeight: "1.3em",
                  textShadow:
                    "0 0 12px rgba(57, 255, 20, 0.55), 0 0 32px rgba(57, 255, 20, 0.28)",
                }}
              >
                <Typewriter
                  words={["Network", "Cloud", "Security", "AI", "Communication"]}
                  typeSpeed={80}
                  eraseSpeed={50}
                  pauseAfterType={1600}
                  color="#39FF14"
                />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="fs-btn-primary" data-testid="hero-cta-primary">
                Talk to an Expert <ArrowRight size={16} />
              </Link>
              <Link to="/services" className="fs-btn-ghost" data-testid="hero-cta-secondary">
                Explore Solutions
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const ServicesGrid = () => (
  <section className="fs-section relative" data-testid="services-section">
    <div className="fs-container relative">
      <div className="max-w-2xl">
        <Reveal>
          <div className="fs-eyebrow">What we do</div>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="fs-heading mt-4 text-3xl md:text-[2.75rem]">
            A complete enterprise stack — connectivity to security.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-5 text-[color:var(--text-dim)] leading-relaxed">
            Sixteen production-grade solution lines, one accountable partner.
            Built for the CIOs, CTOs and IT leaders of India's most demanding
            enterprises.
          </p>
        </Reveal>
      </div>

      <Stagger className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" stagger={0.04}>
        {SERVICES.map((s) => {
          const Icon = s.icon;
          return (
            <StaggerItem key={s.title}>
              <div className="fs-card p-6 h-full" data-testid={`service-${s.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="fs-icon-tile">
                  <Icon size={18} />
                </div>
                <div className="mt-6 fs-tag">{s.tag}</div>
                <h3 className="mt-2 text-[17px] font-semibold text-[color:var(--text)]">
                  {s.title}
                </h3>
                <p className="mt-3 text-[13.5px] text-[color:var(--text-dim)] leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

const IndustriesSection = () => (
  <section className="fs-section relative" data-testid="industries-section">
    <div className="fs-container">
      <div className="max-w-2xl">
        <Reveal>
          <div className="fs-eyebrow">Industries we serve</div>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="fs-heading mt-4 text-3xl md:text-[2.75rem]">
            Trusted across sectors that can't afford downtime.
          </h2>
        </Reveal>
      </div>

      <Stagger className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" stagger={0.04}>
        {INDUSTRIES.map((i) => {
          const Icon = i.icon;
          return (
            <StaggerItem key={i.name}>
              <div className="fs-card p-5 flex items-center gap-4" data-testid={`industry-${i.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                <div className="fs-icon-tile shrink-0">
                  <Icon size={18} />
                </div>
                <span className="text-[color:var(--text)] font-medium text-[15px]">{i.name}</span>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

const WhySection = () => {
  const items = [
    { icon: Compass, title: "Vendor-agnostic advisory", desc: "We recommend what fits your business — not our margin sheet." },
    { icon: ShieldCheck, title: "Carrier-grade SLA", desc: "99.9%+ uptime backed by contractual credits and 24/7 NOC." },
    { icon: Users, title: "Single accountability", desc: "One partner, one SPOC — from licensing to Layer-1." },
    { icon: Sparkles, title: "Future-ready architecture", desc: "SASE, Zero-Trust and AI-ready designs baked in." },
  ];
  return (
    <section className="fs-section relative" data-testid="why-section">
      <div className="fs-container relative grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="fs-eyebrow">Why Four Square</div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="fs-heading mt-4 text-3xl md:text-[2.5rem]">
              A partner engineered for outcomes, not just deliverables.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 text-[color:var(--text-dim)] leading-relaxed">
              Multi-vendor expertise, carrier-grade SLAs, and a dedicated NOC —
              working as an extension of your IT team from day one.
            </p>
          </Reveal>
        </div>

        <Stagger className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5" stagger={0.06}>
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <StaggerItem key={it.title}>
                <div className="fs-card p-6 h-full">
                  <div className="fs-icon-tile"><Icon size={18} /></div>
                  <h3 className="mt-5 text-[17px] font-semibold">{it.title}</h3>
                  <p className="mt-2 text-[13.5px] text-[color:var(--text-dim)] leading-relaxed">
                    {it.desc}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
};

const Home = () => {
  return (
    <div data-testid="page-home">
      <HeroSection />
      <ServicesGrid />
      <IndustriesSection />
      <WhySection />
      <CTASection />
    </div>
  );
};

export default Home;
